import clsx from "clsx";
import type { CSSProperties } from "react";

import { Icon, type IconNameExtended } from "@/components/library/Icon";
import { BUILDER_ICON_TYPE } from "@/puck/clientBlocks";
import type { FloatingBadgeBlockProps } from "@/puck/types";

const ANGLE_CLASSES: Record<FloatingBadgeBlockProps["angle"], string> = {
  "-3": "-rotate-3",
  "-2": "-rotate-2",
  "0": "rotate-0",
  "2": "rotate-2",
  "3": "rotate-3",
};

const DEFAULT_OVERLAP_MARGIN = 40;

function getOverlapStyle(
  isOverlapping: boolean,
  placement: NonNullable<FloatingBadgeBlockProps["placement"]>,
  overlapOffset: number,
): CSSProperties | undefined {
  if (!isOverlapping || overlapOffset <= 0) {
    return undefined;
  }

  return placement === "top"
    ? { marginBottom: `-${overlapOffset}px` }
    : { marginTop: `-${overlapOffset}px` };
}

function FloatingPill({
  title,
  angle,
}: Readonly<{
  title: string;
  angle: FloatingBadgeBlockProps["angle"];
}>) {
  return (
    <div
      className={clsx(
        "inline-flex items-center gap-2 rounded-full border border-black/[0.06] bg-white px-3.5 py-2 shadow-[0_16px_40px_-20px_rgba(30,50,120,0.4)]",
        ANGLE_CLASSES[angle],
      )}
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full bg-gail-primary"
      />
      <span className="font-monument text-sm text-gail-primary">{title}</span>
    </div>
  );
}

function FloatingBadgeCard({
  icon,
  title,
  status,
  angle,
}: Readonly<{
  icon: string;
  title: string;
  status: string;
  angle: FloatingBadgeBlockProps["angle"];
}>) {
  return (
    <div
      className={clsx(
        "flex items-center gap-3 rounded-2xl border border-black/[0.06] bg-white px-4 py-3.5 shadow-[0_20px_50px_-24px_rgba(30,50,120,0.45)]",
        ANGLE_CLASSES[angle],
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gail-primary-light text-gail-primary">
        <Icon
          icon={(icon || "file-pdf") as IconNameExtended}
          type={BUILDER_ICON_TYPE}
          className="h-5 w-5"
          aria-hidden
        />
      </span>
      <div className="min-w-0">
        <p className="truncate font-monument text-sm font-semibold text-black md:text-base">
          {title}
        </p>
        {status ? (
          <p className="mt-0.5 font-monument text-sm text-gail-success">
            {status}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function FloatingBadgeBlock({
  icon,
  title,
  status,
  angle,
  layout,
  align = "left",
  overlapMargin = DEFAULT_OVERLAP_MARGIN,
  placement = "bottom",
  variant = "badge",
}: Readonly<FloatingBadgeBlockProps>) {
  const isOverlapping = layout === "overlapping";
  const overlapOffset = Math.max(0, overlapMargin);

  return (
    <div
      className={clsx(
        variant === "pill" ? "w-auto" : "w-full max-w-xs",
        isOverlapping && "relative z-10",
        isOverlapping && align === "left" && "self-start ml-4 md:ml-8",
        isOverlapping && align === "right" && "self-end ml-auto mr-4 md:mr-8",
        !isOverlapping && align === "right" && "ml-auto",
      )}
      style={getOverlapStyle(isOverlapping, placement, overlapOffset)}
    >
      {variant === "pill" ? (
        <FloatingPill title={title} angle={angle} />
      ) : (
        <FloatingBadgeCard
          icon={icon}
          title={title}
          status={status}
          angle={angle}
        />
      )}
    </div>
  );
}
